import { acceptedNormalizedCourseIds } from "./accepted-course-ids";
import type { Assignment, CalendarEvent } from "../types";

/**
 * The set of dimensions the calendar can be narrowed by for pre-export review.
 * Each is an OR-set of selected values; across dimensions the sets combine with
 * AND (an event must satisfy every non-empty facet). Empty array = no
 * constraint. `students` is populated in the student slice (Phase 4) and matched
 * via an external lookup — the four event-sourced facets need no extra data.
 */
export interface CalendarFilters {
  /** `event.teacherEmail` values. */
  teachers: string[];
  /** `event.location` values (rooms are free-text on the event, not `roomName`). */
  rooms: string[];
  /** Normalized course codes (from {@link acceptedNormalizedCourseIds}). */
  courses: string[];
  /** `event.subjectArea` values. */
  subjects: string[];
  /** Student identifiers — matched via a lookup, since events carry no roster. */
  students: string[];
  /**
   * Grade strings (e.g. `"11"`) — assignment-sourced like {@link students},
   * matched via a lookup that joins a placed student to their grade. A section
   * shows for a grade when a placed student of that grade is in it.
   */
  grades: string[];
}

/**
 * Facet keys as `as const` tuples so the runtime list and the literal-union
 * type share one source of truth (same pattern as `RUN_STATUSES` in types.ts).
 * Adding a facet here updates the union, the persistence iteration, and the
 * `satisfies` check below together — no hand-maintained duplicate to drift.
 */
export const EVENT_FACET_KEYS = ["teachers", "rooms", "courses", "subjects"] as const;
export const FACET_KEYS = [...EVENT_FACET_KEYS, "students", "grades"] as const;
/** The four event-sourced facet keys (everything except students). */
export type EventFacetKey = (typeof EVENT_FACET_KEYS)[number];
export type FacetKey = (typeof FACET_KEYS)[number];

export const EMPTY_CALENDAR_FILTERS = {
  teachers: [],
  rooms: [],
  courses: [],
  subjects: [],
  students: [],
  grades: [],
} satisfies Record<FacetKey, string[]>;

/** One selectable value in a facet dropdown; `label` is what the user sees. */
export interface FilterOption {
  value: string;
  label: string;
}

/** The values a single event contributes to each event-sourced facet. */
function eventTeachers(e: CalendarEvent): string[] {
  return e.teacherEmail ? [e.teacherEmail] : [];
}
function eventRooms(e: CalendarEvent): string[] {
  return e.location ? [e.location] : [];
}
function eventCourses(e: CalendarEvent): string[] {
  return acceptedNormalizedCourseIds(e);
}
function eventSubjects(e: CalendarEvent): string[] {
  return e.subjectArea ? [e.subjectArea] : [];
}

/** True when no facet is constrained (fast passthrough / "unfiltered"). */
export function isFiltersEmpty(filters: CalendarFilters): boolean {
  return (
    filters.teachers.length === 0 &&
    filters.rooms.length === 0 &&
    filters.courses.length === 0 &&
    filters.subjects.length === 0 &&
    filters.students.length === 0 &&
    filters.grades.length === 0
  );
}

/** OR-within a facet: an empty selection is no constraint; otherwise intersect. */
function matchesFacet(selected: string[], eventValues: string[]): boolean {
  if (selected.length === 0) return true;
  return eventValues.some((v) => selected.includes(v));
}

/**
 * Filters `events` by `filters` with AND-across / OR-within semantics. Returns
 * the same array reference when nothing is constrained so identity-based memos
 * upstream stay stable.
 *
 * `studentEventLookup`, when provided, returns the student identifiers assigned
 * to an event's section (built from the run's assignments in Phase 4). It is
 * only consulted when the `students` facet is non-empty, so the earlier slices
 * pass nothing and pay no cost.
 *
 * `gradeEventLookup` mirrors it for the grade facet: given an event, it returns
 * the grade strings of the students placed in its section. Only consulted when
 * the `grades` facet is non-empty.
 */
export function applyCalendarFilters(
  events: CalendarEvent[],
  filters: CalendarFilters,
  studentEventLookup?: (event: CalendarEvent) => string[],
  gradeEventLookup?: (event: CalendarEvent) => string[],
): CalendarEvent[] {
  if (isFiltersEmpty(filters)) return events;
  return events.filter((event) => {
    if (!matchesFacet(filters.teachers, eventTeachers(event))) return false;
    if (!matchesFacet(filters.rooms, eventRooms(event))) return false;
    if (!matchesFacet(filters.courses, eventCourses(event))) return false;
    if (!matchesFacet(filters.subjects, eventSubjects(event))) return false;
    if (filters.students.length > 0) {
      const studentIds = studentEventLookup ? studentEventLookup(event) : [];
      if (!matchesFacet(filters.students, studentIds)) return false;
    }
    if (filters.grades.length > 0) {
      const grades = gradeEventLookup ? gradeEventLookup(event) : [];
      if (!matchesFacet(filters.grades, grades)) return false;
    }
    return true;
  });
}

/**
 * De-duplicate + build options, labelling each value via `labelFor` (identity by
 * default), then sort by the resulting label alphabetically (case-insensitive).
 */
function toOptions(
  values: Iterable<string>,
  labelFor: (value: string) => string = (v) => v,
): FilterOption[] {
  return [...new Set(values)]
    .map((value) => ({ value, label: labelFor(value) }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
}

/**
 * Derives the selectable option lists for the four event-sourced facets from
 * the currently loaded events. The option value IS the match key (e.g. the
 * teacher email, the free-text room, the normalized course code), so a selection
 * always corresponds to something present in the loaded window — no field
 * mismatch and no "phantom" options for entities absent from the view.
 *
 * `courseNames` maps a normalized course code to its friendly title (from
 * sections); when supplied, course options are labelled by name (falling back to
 * the code) while still keyed by code. Other facets have no separate display name.
 */
export function deriveFacetOptions(
  events: CalendarEvent[],
  courseNames?: ReadonlyMap<string, string>,
): Record<EventFacetKey, FilterOption[]> {
  const teachers = new Set<string>();
  const rooms = new Set<string>();
  const courses = new Set<string>();
  const subjects = new Set<string>();
  for (const e of events) {
    for (const t of eventTeachers(e)) teachers.add(t);
    for (const r of eventRooms(e)) rooms.add(r);
    for (const c of eventCourses(e)) courses.add(c);
    for (const s of eventSubjects(e)) subjects.add(s);
  }
  return {
    teachers: toOptions(teachers),
    rooms: toOptions(rooms),
    courses: toOptions(courses, (code) => courseNames?.get(code) ?? code),
    subjects: toOptions(subjects),
  };
}

/**
 * Join key linking an assignment to an event's section. Assumes neither a
 * normalized course code (upper-alphanumeric) nor a section label contains `|`;
 * safe for the current data, revisit the delimiter if those fields ever widen.
 */
function sectionKey(normalizedCourseId: string, sectionLabel: string | null): string {
  return `${normalizedCourseId}|${sectionLabel ?? ""}`;
}

/**
 * Builds the student-facet lookup: given a run's assignments, returns a function
 * mapping an event to the identifiers of students placed in its section. Uses
 * the same `(accepted normalized course id, sectionLabel ?? "")` join as
 * {@link computeSectionRoster}, so co-listed sections and null labels match
 * consistently. Events carry no roster, so this is the only way to filter by
 * student.
 */
export function buildStudentEventLookup(
  assignments: readonly Assignment[],
): (event: CalendarEvent) => string[] {
  const bySection = new Map<string, Set<string>>();
  for (const a of assignments) {
    const key = sectionKey(a.normalizedCourseId, a.sectionLabel);
    let set = bySection.get(key);
    if (!set) {
      set = new Set<string>();
      bySection.set(key, set);
    }
    set.add(a.studentIdentifier);
  }
  return (event: CalendarEvent) => {
    const label = event.section ?? "";
    const students = new Set<string>();
    for (const code of acceptedNormalizedCourseIds(event)) {
      const set = bySection.get(`${code}|${label}`);
      if (set) for (const s of set) students.add(s);
    }
    return [...students];
  };
}

/**
 * Student facet options from a run's assignments: one per distinct student,
 * labelled by `studentName` when present (falling back to the identifier), and
 * sorted by that label. `value` is always the identifier — the stable match key.
 */
export function deriveStudentOptions(
  assignments: readonly Assignment[],
): FilterOption[] {
  const labelById = new Map<string, string>();
  for (const a of assignments) {
    if (!labelById.has(a.studentIdentifier)) {
      labelById.set(a.studentIdentifier, a.studentName ?? a.studentIdentifier);
    }
  }
  return [...labelById.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((x, y) =>
      x.label.localeCompare(y.label, undefined, { sensitivity: "base" }),
    );
}

/**
 * Builds the grade-facet lookup: given a run's assignments and a map from a
 * normalized student id to that student's grade, returns a function mapping an
 * event to the distinct grade strings of the students placed in its section.
 *
 * Structurally identical to {@link buildStudentEventLookup}, but collects grade
 * strings instead of identifiers. The grade for an assignment is resolved via
 * `gradeById.get(a.normalizedStudentIdentifier)` — the join keys on the
 * *normalized* student id, never the raw identifier (the load-bearing join).
 * Assignments whose student has no grade (absent from the map) contribute
 * nothing, so a section whose only placed students are grade-less yields `[]`.
 */
export function buildGradeEventLookup(
  assignments: readonly Assignment[],
  gradeById: ReadonlyMap<string, number>,
): (event: CalendarEvent) => string[] {
  const bySection = new Map<string, Set<string>>();
  for (const a of assignments) {
    const grade = gradeById.get(a.normalizedStudentIdentifier);
    if (grade === undefined) continue;
    const key = sectionKey(a.normalizedCourseId, a.sectionLabel);
    let set = bySection.get(key);
    if (!set) {
      set = new Set<string>();
      bySection.set(key, set);
    }
    set.add(String(grade));
  }
  return (event: CalendarEvent) => {
    const label = event.section ?? "";
    const grades = new Set<string>();
    for (const code of acceptedNormalizedCourseIds(event)) {
      const set = bySection.get(`${code}|${label}`);
      if (set) for (const g of set) grades.add(g);
    }
    return [...grades];
  };
}

/**
 * Grade facet options from a run's assignments: one per distinct grade among
 * *placed & graded* students (grades reachable through the same
 * `assignment → gradeById` join as {@link buildGradeEventLookup}). `value` is
 * the grade string (e.g. `"11"`), `label` is `"Grade 11"`.
 *
 * Sorted **numerically** by `Number(value)` — a dedicated sort, not the generic
 * {@link toOptions} whose lexical order would place `"9"` after `"12"`.
 */
export function deriveGradeOptions(
  assignments: readonly Assignment[],
  gradeById: ReadonlyMap<string, number>,
): FilterOption[] {
  const grades = new Set<string>();
  for (const a of assignments) {
    const grade = gradeById.get(a.normalizedStudentIdentifier);
    if (grade === undefined) continue;
    grades.add(String(grade));
  }
  return [...grades]
    .sort((a, b) => Number(a) - Number(b))
    .map((value) => ({ value, label: `Grade ${value}` }));
}
